import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from 'baseui';
import useFeatureFlag from '../../../../../common/hooks/useFeatureFlag';
import { FEATURE_FLAGS, LAYER_ZINDEX } from '../../../constants';
import useCanvas from '../../../providers/hooks/use-canvas';
import type { BrushAnnotationT } from '../../../types';
import { isFeatureEnabled } from '../../../utils/isFeatureEnabled';
import { type AnnotationPixelMap } from '../atoms/segmentation-atoms';
import { cleanupCanvas, createColoredSegment, createSegmentPreviewCanvas, findTranslatedSegmentData, imageDataToBase64String, invalidateOutlineCache, pickIdAt } from './utils';
const OverlayContainer = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: LAYER_ZINDEX + 1
});
type DragStateT = {
  title: string;
  annotationId: string;
  startX: number;
  startY: number;
  dx: number;
  dy: number;
};
type SegmentationSelectionOverlayProps = {
  currentFrame: number;
  annotationBitmap: Record<string, ImageData>;
  annotationPixelMap: AnnotationPixelMap | undefined;
  semanticAnnotations: Record<string, BrushAnnotationT>;
  selectedTool: string;
  selectAnnotation: (id: string | null) => void;
  updateAnnotation: (id: string, annotation: BrushAnnotationT) => void;
};
const SegmentationSelectionOverlay = ({
  currentFrame,
  annotationBitmap,
  annotationPixelMap,
  semanticAnnotations,
  selectedTool,
  selectAnnotation,
  updateAnnotation
}: SegmentationSelectionOverlayProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const boundsRef = useRef<DOMRect | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [dragState, setDragState] = useState<DragStateT | null>(null);
  const animationFrameIdRef = useRef<number | null>(null);
  const {
    getDrawingCanvasContext,
    getCanvasContext
  } = useCanvas();
  const segmentationUserEventsFlag = useFeatureFlag(FEATURE_FLAGS.SEGMENTATION_USER_EVENTS);
  const segmentationUserEventsEnabled = isFeatureEnabled(FEATURE_FLAGS.SEGMENTATION_USER_EVENTS, segmentationUserEventsFlag);
  const titleToAnnotationId = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(semanticAnnotations).forEach(([annotationId, annotation]) => {
      map[annotation.annotationLabel.title] = annotationId;
    });
    return map;
  }, [semanticAnnotations]);
  const pickAt = useCallback((e: React.PointerEvent) => {
    if (!annotationPixelMap || !containerRef.current) return {
      title: (null as string | null),
      annotationId: (null as string | null),
      px: 0,
      py: 0
    };
    const rect = containerRef.current.getBoundingClientRect();
    const scaleX = annotationPixelMap.width / rect.width;
    const scaleY = annotationPixelMap.height / rect.height;
    const px = Math.floor((e.clientX - rect.left) * scaleX);
    const py = Math.floor((e.clientY - rect.top) * scaleY);
    const pid = pickIdAt(px, py, annotationPixelMap);
    if (!pid) return {
      title: null,
      annIdx: null,
      px,
      py
    };
    const title = annotationPixelMap.idToTitle[pid];
    const annotationId = title && titleToAnnotationId[title] || null;
    return {
      title,
      annotationId,
      px,
      py
    };
  }, [annotationPixelMap, titleToAnnotationId]);
  const renderDragPreview = useCallback(() => {
    const drawingCtx = getDrawingCanvasContext();
    const previewCanvas = previewCanvasRef.current;
    if (!drawingCtx || !dragState || !previewCanvas) return;
    const hasMovement = (dragState.dx | 0) !== 0 || (dragState.dy | 0) !== 0;
    if (!hasMovement) return;
    drawingCtx.clearRect(0, 0, drawingCtx.canvas.width, drawingCtx.canvas.height);
    drawingCtx.save();
    drawingCtx.globalAlpha = 1;
    drawingCtx.drawImage(previewCanvas, dragState.dx | 0, dragState.dy | 0);
    drawingCtx.restore();
  }, [dragState, getDrawingCanvasContext]);
  const initializeSegmentPreview = useCallback((segmentTitle: string, annotationId: string) => {
    const segmentData = (annotationBitmap || {})[segmentTitle];
    if (!segmentData) return null;
    const previewCanvas = createSegmentPreviewCanvas(segmentData.width, segmentData.height);
    const previewCtx = previewCanvas.getContext('2d');
    if (!previewCtx) return null;
    const annotation = semanticAnnotations[annotationId];
    const segmentColor = annotation?.annotationLabel?.style?.fillColor || annotation?.annotationLabel?.style?.strokeColor;
    if (segmentColor) {
      const coloredSegment = createColoredSegment(segmentData, segmentColor);
      previewCtx.putImageData(coloredSegment, 0, 0);
    }
    previewCanvasRef.current = previewCanvas;
    return previewCanvas;
  }, [semanticAnnotations, annotationBitmap, previewCanvasRef]);
  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    boundsRef.current = containerRef.current?.getBoundingClientRect() || null;
    if (!boundsRef.current) return;
    const {
      title,
      annotationId,
      px,
      py
    } = pickAt(e);
    if (!title || !annotationId) {
      selectAnnotation(null);
      if (e.target instanceof HTMLElement) {
        e.target.style.pointerEvents = 'none';
        const elementBelow = document.elementFromPoint(e.clientX, e.clientY);
        e.target.style.pointerEvents = 'auto';
        if (elementBelow) {
          elementBelow.dispatchEvent(new PointerEvent('pointerdown', {
            bubbles: true,
            cancelable: true,
            clientX: e.clientX,
            clientY: e.clientY
          }));
        }
      }
      return;
    }
    selectAnnotation(annotationId);
    const previewCanvas = initializeSegmentPreview(title, annotationId);
    if (!previewCanvas) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragState({
      title,
      annotationId,
      startX: px,
      startY: py,
      dx: 0,
      dy: 0
    });
  }, [pickAt, selectAnnotation, initializeSegmentPreview, setDragState]);
  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragState || !annotationPixelMap || !boundsRef.current) return;
    const bounds = boundsRef.current;
    const scaleX = annotationPixelMap.width / bounds.width;
    const scaleY = annotationPixelMap.height / bounds.height;
    const currentX = (e.clientX - bounds.left) * scaleX;
    const currentY = (e.clientY - bounds.top) * scaleY;
    setDragState(prev => prev ? {
      ...prev,
      dx: currentX - prev.startX,
      dy: currentY - prev.startY
    } : prev);
  }, [dragState, setDragState, annotationPixelMap]);
  const onPointerUp = useCallback(async (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragState) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    const hasMovement = (dragState.dx | 0) !== 0 || (dragState.dy | 0) !== 0;
    const drawingCtx = getDrawingCanvasContext();
    const mainCtx = getCanvasContext();
    if (!hasMovement) {
      drawingCtx?.clearRect(0, 0, mainCtx?.canvas.width || 0, mainCtx?.canvas.height || 0);
      setDragState(null);
      return;
    }
    const previewCanvas = previewCanvasRef.current;
    if (!mainCtx || !previewCanvas) {
      setDragState(null);
      return;
    }
    const translatedSegment = findTranslatedSegmentData(previewCanvas, dragState.dx, dragState.dy, mainCtx.canvas.width, mainCtx.canvas.height);
    invalidateOutlineCache(dragState.title);
    const annotation = semanticAnnotations[dragState.annotationId];
    if (annotation) {
      updateAnnotation(dragState.annotationId, {
        ...annotation,
        data: {
          ...annotation.data,
          base64String: imageDataToBase64String(translatedSegment)
        }
      });
    }
    drawingCtx?.clearRect(0, 0, drawingCtx.canvas.width, drawingCtx.canvas.height);
    cleanupCanvas(previewCanvasRef.current);
    previewCanvasRef.current = null;
    setDragState(null);
  }, [dragState, getDrawingCanvasContext, getCanvasContext, semanticAnnotations, currentFrame, updateAnnotation]);
  useEffect(() => {
    if (!dragState) {
      if (animationFrameIdRef.current !== null) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
      return;
    }
    if (animationFrameIdRef.current !== null) {
      cancelAnimationFrame(animationFrameIdRef.current);
    }
    animationFrameIdRef.current = requestAnimationFrame(renderDragPreview);
    return () => {
      if (animationFrameIdRef.current !== null) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    };
  }, [dragState, renderDragPreview]);
  if (selectedTool !== 'POINTER' || Object.keys(semanticAnnotations).length === 0) return null;
  return <OverlayContainer ref={containerRef} onPointerDown={onPointerDown} {...segmentationUserEventsEnabled ? {
    onPointerMove
  } : {}} {...segmentationUserEventsEnabled ? {
    onPointerUp
  } : {}} />;
};
export default SegmentationSelectionOverlay;